import { useEffect, useMemo, useRef, useState } from "react";
import { MagnifyingGlass, X, IdentificationCard } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";

/**
 * EmployeePicker — dropdown compacto para elegir un colaborador de nómina
 * activo. Al elegirlo llama a `onPick(emp)` con {id, name, dni, position}
 * para que el caller autopobre los campos del formulario.
 *
 * Uso: <EmployeePicker onPick={(emp) => …} placeholder="Buscar…" testid="…" />
 *
 * Comportamiento:
 *  - Carga la lista de empleados activos una sola vez al montar.
 *  - Filtra en cliente por nombre/DNI/cuadrilla/puesto (case-insensitive).
 *  - Muestra máximo 6 sugerencias.
 *  - Botón X para limpiar el input.
 */
export default function EmployeePicker({
  onPick,
  placeholder = "Buscar en nómina…",
  testid = "employee-picker",
  autoFocus = false,
}) {
  const [emps, setEmps] = useState([]);
  const [q, setQ] = useState("");
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    api.get("/hr/employees")
      .then((r) => setEmps((r.data || []).filter((e) => e.status === "active")))
      .catch(() => setEmps([]));
  }, []);

  useEffect(() => {
    const onDoc = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const filtered = useMemo(() => {
    const s = q.trim().toLowerCase();
    if (!s) return [];
    return emps.filter((e) =>
      (e.name || "").toLowerCase().includes(s)
      || (e.dni || "").toLowerCase().includes(s)
      || (e.position || "").toLowerCase().includes(s)
      || (e.crew || "").toLowerCase().includes(s)
    ).slice(0, 6);
  }, [emps, q]);

  const choose = (emp) => {
    onPick?.(emp);
    setQ("");
    setOpen(false);
  };

  return (
    <div ref={ref} className="relative" data-testid={`${testid}-wrapper`}>
      <div className="relative">
        <MagnifyingGlass size={14} weight="bold" className="absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
        <Input
          value={q}
          onChange={(e) => { setQ(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder={placeholder}
          className="pl-7 h-8 text-xs bg-white"
          autoFocus={autoFocus}
          data-testid={`${testid}-input`}
        />
        {q && (
          <button
            onClick={() => { setQ(""); setOpen(false); }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
            data-testid={`${testid}-clear`}
          >
            <X size={12} weight="bold" />
          </button>
        )}
      </div>
      {open && filtered.length > 0 && (
        <ul
          className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded shadow-lg max-h-60 overflow-y-auto"
          data-testid={`${testid}-results`}
        >
          {filtered.map((e) => (
            <li key={e.id}>
              <button
                onClick={() => choose(e)}
                className="w-full text-left px-2 py-1.5 hover:bg-blue-50 flex items-center gap-2 text-xs"
                data-testid={`${testid}-item-${e.id}`}
              >
                <IdentificationCard size={14} weight="fill" className="text-blue-900 shrink-0" />
                <div className="flex-1 min-w-0">
                  <p className="font-semibold text-slate-900 truncate">{e.name}</p>
                  <p className="text-[10px] text-slate-500 truncate">
                    {e.position || "-"} · Cuadr. {e.crew || "-"} · DNI {e.dni || "-"}
                  </p>
                </div>
              </button>
            </li>
          ))}
        </ul>
      )}
      {open && q && filtered.length === 0 && (
        <p className="absolute z-30 top-full left-0 right-0 mt-1 bg-white border border-slate-200 rounded px-2 py-1.5 text-[11px] text-slate-500 italic">
          Sin coincidencias — puedes escribir manualmente al lado.
        </p>
      )}
    </div>
  );
}
